import React, { useState } from "react";

import { useLocalization } from "@fluent/react";
import {
  Alert,
  Radio,
  Spinner,
  TextInput,
  formatNumber,
  useDropdownLayer,
} from "@narmi/design_system";
import cc from "classcat";

import { useAccountsContext } from "../../../../../components/contexts/AccountContext";
import { formatReceiptDate } from "../../format";
import { useTransactionSearch } from "../../hooks/useTransactionSearch";

import styles from "./SelectTransactionModal.module.scss";

const LISTBOX_ID = "select-transaction-search-listbox";

const optionId = (transactionId: string) =>
  `select-transaction-search-option-${transactionId}`;

const transactionDescription = (transaction: API.Transaction) =>
  transaction.description || transaction.raw_description;

// Transaction amounts are signed integer cents; these displays are a
// magnitude for a person reading "how much", matching the candidate rows.
const formatTransactionAmount = (cents: number) =>
  formatNumber(Math.abs(cents) / 100, "currency");

interface TransactionSearchSectionProps {
  selectedTransaction: API.Transaction | null;
  isSelected: boolean;
  showSwapNotice: boolean;
  onSelect: (transaction: API.Transaction) => void;
}

const TransactionSearchSection = ({
  selectedTransaction,
  isSelected,
  showSwapNotice,
  onSelect,
}: TransactionSearchSectionProps) => {
  const { l10n } = useLocalization();
  const { accounts } = useAccountsContext();
  const [searchInput, setSearchInput] = useState("");
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const { results, isSearching } = useTransactionSearch(searchInput);

  const hasQuery = searchInput.trim().length > 0;
  const showDropdown =
    isDropdownOpen && hasQuery && (isSearching || results.length > 0);

  // A fresh response can shrink the list below the highlighted index while
  // keepPreviousData rows are on screen.
  const highlightedIndex =
    results.length > 0 ? Math.min(activeIndex, results.length - 1) : 0;

  const { anchorProps, layerProps } = useDropdownLayer({
    isOpen: showDropdown,
    setIsOpen: (open) => {
      if (!open) setIsDropdownOpen(false);
    },
    ariaPopupType: "listbox",
    placement: "bottom",
    matchWidth: true,
  });

  const selectResult = (transaction: API.Transaction) => {
    onSelect(transaction);
    setSearchInput("");
    setIsDropdownOpen(false);
    setActiveIndex(0);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value);
    setActiveIndex(0);
    setIsDropdownOpen(event.target.value.trim().length > 0);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      if (results.length > 0) {
        setActiveIndex((highlightedIndex + 1) % results.length);
      }
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      if (results.length > 0) {
        setActiveIndex(
          (highlightedIndex - 1 + results.length) % results.length
        );
      }
    } else if (event.key === "Enter") {
      event.preventDefault();
      const active = results[highlightedIndex];
      if (active) selectResult(active);
    } else if (event.key === "Escape") {
      // Close only the dropdown; without stopPropagation the Dialog would
      // dismiss on the same keypress.
      event.preventDefault();
      event.stopPropagation();
      setIsDropdownOpen(false);
    }
  };

  const activeOptionId =
    showDropdown && results[highlightedIndex]
      ? optionId(results[highlightedIndex].id)
      : undefined;

  const accountName = selectedTransaction
    ? (accounts ?? [])
        .find((account) => account.id === selectedTransaction.account_id)
        ?.getShortDescription(false)
    : undefined;

  return (
    <div>
      <p className="fontColor--heading margin--bottom--s">
        {l10n.getString("receipts-select-transaction-search-heading")}
      </p>
      <div
        ref={anchorProps.ref as React.Ref<HTMLDivElement>}
        style={anchorProps.style}
      >
        <TextInput
          label={l10n.getString("receipts-select-transaction-search-label")}
          startIcon="search"
          value={searchInput}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={() => setIsDropdownOpen(false)}
          onFocus={() => {
            if (searchInput.trim().length > 0) setIsDropdownOpen(true);
          }}
          role="combobox"
          aria-expanded={showDropdown}
          aria-controls={LISTBOX_ID}
          aria-activedescendant={activeOptionId}
          aria-autocomplete="list"
          required={false}
          testId="select-transaction-search-input"
        />
      </div>
      <ul
        className={styles["search-dropdown"]}
        role="listbox"
        id={LISTBOX_ID}
        aria-label={l10n.getString(
          "receipts-select-transaction-search-heading"
        )}
        ref={layerProps.ref as React.Ref<HTMLUListElement>}
        style={{
          ...(layerProps.style as React.CSSProperties),
          ...(showDropdown ? {} : { display: "none" }),
        }}
        // Keep focus on the combobox input when clicking rows or dragging the
        // dropdown scrollbar; blur would close the dropdown mid-interaction.
        onMouseDown={(event) => event.preventDefault()}
      >
        {showDropdown &&
          results.map((transaction, index) => (
            // Keyboard interaction lives on the combobox input, per the ARIA
            // combobox pattern; the option rows are pointer targets only.
            <li
              key={transaction.id}
              id={optionId(transaction.id)}
              role="option"
              aria-selected={index === highlightedIndex}
              className={cc([
                styles["search-option"],
                {
                  [styles["search-option--active"]]: index === highlightedIndex,
                },
              ])}
              data-testid={optionId(transaction.id)}
              onClick={() => selectResult(transaction)}
              onMouseEnter={() => setActiveIndex(index)}
            >
              <span className={styles["search-option-main"]}>
                <span className="fontColor--heading">
                  {transactionDescription(transaction)}
                </span>
                <span className="fontColor--secondary fontSize--s">
                  {formatReceiptDate(transaction.settled_at)}
                </span>
              </span>
              <span className="fontColor--heading">
                {formatTransactionAmount(transaction.amount)}
              </span>
            </li>
          ))}
        {showDropdown && isSearching && results.length === 0 && (
          <li
            role="option"
            aria-selected={false}
            aria-disabled
            className={styles["search-loading"]}
            aria-label={l10n.getString(
              "receipts-select-transaction-search-loading"
            )}
            data-testid="select-transaction-search-loading"
          >
            <Spinner size={20} color="var(--color-mediumGrey)" />
          </li>
        )}
      </ul>
      {showSwapNotice && (
        <div
          className="margin--top--l"
          data-testid="select-transaction-swap-notice"
        >
          <Alert isActive kind="warn" isDismissable={false}>
            {l10n.getString("receipts-select-transaction-swap-notice")}
          </Alert>
        </div>
      )}
      {selectedTransaction && (
        <div className="margin--top--l">
          <Radio
            name="select-transaction-search-selection"
            value={selectedTransaction.id}
            kind="input-card"
            checked={isSelected}
            onCheck={() => onSelect(selectedTransaction)}
          >
            <div
              className={styles["candidate-content"]}
              data-testid="select-transaction-search-selected"
            >
              <div className={styles["candidate-line"]}>
                <span className="fontColor--heading fontWeight--semibold">
                  {transactionDescription(selectedTransaction)}
                </span>
                <span className="fontColor--heading fontWeight--semibold">
                  {formatTransactionAmount(selectedTransaction.amount)}
                </span>
              </div>
              <div className={styles["candidate-line"]}>
                <span className="fontColor--primary fontSize--s">
                  {formatReceiptDate(selectedTransaction.settled_at)}
                  {accountName ? ` · ${accountName}` : ""}
                </span>
              </div>
            </div>
          </Radio>
        </div>
      )}
    </div>
  );
};

export default TransactionSearchSection;
